"use client";

import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/lib/utils";
import { useMemo } from "react";
import { usePdpCheapestOffer } from "@/lib/react-query/queries/accommodation/use-pdp-prices";

const colorCombinations = [
  {
    bg: "bg-alert-default-50",
    text: "text-alert-default-500",
  },
  {
    bg: "bg-alert-success-50",
    text: "text-alert-success-500",
  },
  {
    bg: "bg-alert-info-50",
    text: "text-alert-info-500",
  },
  {
    bg: "bg-alert-warning-50",
    text: "text-alert-warning-500",
  },
  {
    bg: "bg-alert-error-50",
    text: "text-alert-error-500",
  },
];

function getRandomColorForHost(hostName: string) {
  // Use host name as seed for consistent color selection
  let hash = 0;
  for (let i = 0; i < hostName.length; i++) {
    const char = hostName.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  const index = Math.abs(hash) % colorCombinations.length;
  return colorCombinations[index];
}

export function AccommodationHost({
  accommodationId,
  className,
}: {
  accommodationId: string;
  className?: string;
}) {
  const { offer } = usePdpCheapestOffer(accommodationId);
  const hostName = offer?.origin?.name || "--";
  const avatarColors = useMemo(
    () => getRandomColorForHost(hostName),
    [hostName],
  );

  return (
    <div
      className={cn(
        "flex px-[18px] py-2 md:py-[12px] gap-3 rounded-xl border border-gray-100 items-center justify-between",
        className,
      )}
    >
      <div className="space-y-1">
        <Typography className="text-[10px] font-normal md:text-sm md:font-medium">
          میزبان
        </Typography>
        <Typography
          variant="captionMedium"
          className="md:text-base md:font-normal"
        >
          {hostName}
        </Typography>
      </div>
      <Avatar className="size-12 md:size-16">
        <AvatarImage alt={hostName} />
        <AvatarFallback className={`${avatarColors.bg} ${avatarColors.text}`}>
          {hostName[0]}
        </AvatarFallback>
      </Avatar>
    </div>
  );
}
